import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import PatientService from '../../../services/patientService';
import EvolutionNoteService from '../../../services/evolutionNoteService';
import './EvolutionNotes.css';

const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const EvolutionNotes = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const [signModal, setSignModal] = useState({ show: false, noteId: null });
  const [signing, setSigning] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, noteId: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    const load = async () => {
      const token = auth.user?.access_token;
      if (!token) { setIsLoading(false); return; }
      try {
        const [patientData, notesResponse] = await Promise.all([
          PatientService.getPatientById(patientId, token),
          EvolutionNoteService.getNotesByPatient(patientId, token)
        ]);
        setPatient(patientData);
        setNotes(notesResponse.data || []);
      } catch {
        setError('Error al cargar las notas de evolución');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [patientId, auth.user]);

  const patientName = patient
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    : null;

  const handleDeleteConfirm = async () => {
    const token = auth.user?.access_token;
    if (!token) return;
    setDeleting(true);
    try {
      await EvolutionNoteService.deleteNote(deleteModal.noteId, token);
      setNotes(prev => prev.filter(n => n.id !== deleteModal.noteId));
      setDeleteModal({ show: false, noteId: null });
    } catch (err) {
      alert(err.message || 'Error al eliminar la nota');
    } finally {
      setDeleting(false);
    }
  };

  const handleSignConfirm = async () => {
    const token = auth.user?.access_token;
    if (!token) return;
    setSigning(true);
    try {
      const result = await EvolutionNoteService.signNote(signModal.noteId, token);
      setNotes(prev => prev.map(n => n.id === signModal.noteId ? result.data : n));
      setSignModal({ show: false, noteId: null });
    } catch (err) {
      alert(err.message || 'Error al firmar la nota');
    } finally {
      setSigning(false);
    }
  };

  if (error && !patient) {
    return (
      <div className="evolution-notes-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="evolution-notes-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patientName}
        sectionTitle="Notas de Evolución"
        actionButton={
          <Button
            variant="primary"
            icon="fas fa-plus"
            onClick={() => navigate(`/patients/${patientId}/evolution-notes/new`)}
          >
            Nueva Nota
          </Button>
        }
      />

      {isLoading ? (
        <div className="evolution-notes-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="evolution-notes-empty">
          <div className="empty-content">
            <i className="fas fa-notes-medical"></i>
            <h3>Sin notas de evolución</h3>
            <p>Este paciente aún no tiene notas de evolución registradas.</p>
          </div>
        </div>
      ) : (
        <div className="evolution-notes-feed">
          {notes.map(note => (
            <div key={note.id} className={`en-item ${note.status}`}>
              <div className="en-timeline">
                <div className={`en-dot ${note.status}`}></div>
                <div className="en-line"></div>
              </div>

              <div className="en-card">
                <div className="en-meta">
                  <div className="en-meta-left">
                    <span className="en-doctor">
                      <i className="fas fa-user-md"></i> {note.doctorName}
                    </span>
                    <span className="en-date">{formatDateTime(note.createdAt)}</span>
                  </div>
                  {note.status === 'signed' ? (
                    <span className="evolution-badge signed">
                      <i className="fas fa-check-circle"></i> Firmada
                    </span>
                  ) : (
                    <span className="evolution-badge draft">
                      <i className="fas fa-pencil-alt"></i> Borrador
                    </span>
                  )}
                </div>

                <p className="en-content">
                  {note.content || <span className="en-empty-text">Sin contenido</span>}
                </p>
                {note.status === 'signed' && note.signedAt && (
                  <p className="en-signed-info">
                    <i className="fas fa-lock"></i> Firmada el {formatDateTime(note.signedAt)}
                  </p>
                )}
                {note.status === 'draft' && (
                  <div className="en-actions">
                    <button
                      className="btn btn-primary btn-icon btn-sm"
                      onClick={() => navigate(`/patients/${patientId}/evolution-notes/${note.id}/edit`)}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-primary btn-icon btn-sm"
                      onClick={() => setSignModal({ show: true, noteId: note.id })}
                      title="Firmar"
                    >
                      <i className="fas fa-check"></i>
                    </button>
                    <button
                      className="btn btn-danger btn-icon btn-sm"
                      onClick={() => setDeleteModal({ show: true, noteId: note.id })}
                      title="Eliminar"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteModal.show && (
        <div className="evolution-modal-overlay" onClick={() => !deleting && setDeleteModal({ show: false, noteId: null })}>
          <div className="evolution-modal-content" onClick={e => e.stopPropagation()}>
            <div className="evolution-modal-header">
              <h3>Eliminar nota de evolución</h3>
              <button className="evolution-modal-close" onClick={() => !deleting && setDeleteModal({ show: false, noteId: null })}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="evolution-modal-body">
              <p>¿Estás seguro de que deseas eliminar esta nota? Esta acción no se puede deshacer.</p>
            </div>
            <div className="evolution-modal-actions">
              <Button variant="secondary" onClick={() => setDeleteModal({ show: false, noteId: null })} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="danger" icon="fas fa-trash" onClick={handleDeleteConfirm} loading={deleting}>
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}

      {signModal.show && (
        <div className="evolution-modal-overlay" onClick={() => !signing && setSignModal({ show: false, noteId: null })}>
          <div className="evolution-modal-content" onClick={e => e.stopPropagation()}>
            <div className="evolution-modal-header">
              <h3>Firmar nota de evolución</h3>
              <button className="evolution-modal-close" onClick={() => !signing && setSignModal({ show: false, noteId: null })}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="evolution-modal-body">
              <p>Al firmar esta nota quedará <strong>bloqueada permanentemente</strong> y no podrá ser modificada. ¿Deseas continuar?</p>
            </div>
            <div className="evolution-modal-actions">
              <Button variant="secondary" onClick={() => setSignModal({ show: false, noteId: null })} disabled={signing}>
                Cancelar
              </Button>
              <Button variant="primary" icon="fas fa-check" onClick={handleSignConfirm} loading={signing}>
                Confirmar firma
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvolutionNotes;
